import { For, JSX, Show, createEffect, createSignal, onCleanup, onMount } from "solid-js";
import { Portal } from "solid-js/web";
import ts from "../styles/tooltip.module.css";

// Hover-triggered text tooltip with the app's styled card chrome.
// Wrap a trigger element (button, link, anything) and pass the text;
// the tooltip Portal-mounts to <body> on hover after a 500ms delay
// so it matches the calendar's lane / date-event tooltip cadence.
// The wrapper is `display: contents` so it doesn't introduce layout.
// On mount the tooltip is measured and the position is clamped to the
// viewport — for triggers near the right or bottom edge it flips to
// the opposite side of the cursor so the card never lands off-screen.

const SHOW_DELAY_MS = 500;
const EDGE_PADDING_PX = 8;
const CURSOR_GAP_PX = 14;

export interface TextTooltipProps {
  /** Primary headline (bold, 0.82rem). */
  text: string;
  /** Optional secondary content beneath the headline (mono, slate).
   *  Pass an array to render each entry on its own line. */
  description?: string | string[] | undefined;
  /** Optional key-value rows rendered as a two-column grid beneath the
   *  headline / description, with labels left-aligned to a shared
   *  column and values aligned next to them. */
  properties?: { label: string; value: string }[] | undefined;
  /** Override colour for the left accent stripe. Falls back to the
   *  app's primary gradient base when omitted. */
  stripe?: string | undefined;
  /** Suppress the tooltip entirely (e.g. when the trigger's own popup
   *  menu is open and the hint would compete with the menu). Pass a
   *  reactive accessor / signal expression so the suppression updates
   *  live with the trigger's open state. */
  disabled?: boolean | undefined;
  /** Show the tooltip on touch tap (pointerType === "touch") with no
   *  delay, dismiss on outside tap. Off by default — opt in for
   *  status/info triggers where mobile users would otherwise never see
   *  the hint (no hover). The wrapped element's onClick still fires. */
  mobileTap?: boolean | undefined;
  children: JSX.Element;
}

export function TextTooltip(props: TextTooltipProps) {
  const [visible, setVisible] = createSignal(false);
  const [ready, setReady] = createSignal(false);
  const [pos, setPos] = createSignal<{ x: number; y: number }>({ x: 0, y: 0 });
  let cursorX = 0;
  let cursorY = 0;
  let tipEl: HTMLDivElement | undefined;
  let triggerWrapEl: HTMLSpanElement | undefined;
  let timer: number | null = null;
  // Latches once a touch tap has shown the tooltip — keeps it visible
  // even if `disabled` flips true (e.g. the wrapped button's onClick
  // opens a menu). Cleared on dismiss.
  const [touchShown, setTouchShown] = createSignal(false);

  const cancel = () => {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  // Compute a viewport-clamped tooltip position given the most recent
  // cursor coords + the measured tooltip rect. Below the cursor by
  // default; flips to the left or above when the default would
  // overflow. Called once before the tooltip mounts (with the cursor's
  // current position) and again after mount (with the real rect), then
  // frozen — tooltips don't track the cursor once visible.
  function reposition() {
    const rect = tipEl?.getBoundingClientRect();
    const w = rect?.width ?? 0;
    const h = rect?.height ?? 0;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = cursorX + CURSOR_GAP_PX;
    let y = cursorY + CURSOR_GAP_PX;
    if (w > 0 && x + w > vw - EDGE_PADDING_PX) {
      x = Math.max(EDGE_PADDING_PX, cursorX - w - CURSOR_GAP_PX);
    }
    if (h > 0 && y + h > vh - EDGE_PADDING_PX) {
      y = Math.max(EDGE_PADDING_PX, cursorY - h - CURSOR_GAP_PX);
    }
    setPos({ x, y });
  }

  const onMove = (e: MouseEvent) => {
    if (props.disabled) return;
    cursorX = e.clientX;
    cursorY = e.clientY;
    // Once shown the tooltip stays put — only update position while we
    // are still in the pre-show delay phase. Without this guard the
    // tooltip jitters with every mousemove.
    if (visible()) return;
    reposition();
    if (timer != null) return;
    timer = window.setTimeout(() => {
      timer = null;
      setVisible(true);
    }, SHOW_DELAY_MS);
  };

  const onLeave = () => {
    if (touchShown()) return;  // touch path uses outside-tap, not mouseleave
    cancel();
    setVisible(false);
    setReady(false);
  };

  // Hide if the trigger's popup opens while we're showing — competing
  // tooltips + menus look fussy. Reactive on props.disabled. Honoured
  // for the hover path only; a touch tap latches the tooltip visible.
  createEffect(() => {
    if (props.disabled && !touchShown()) {
      cancel();
      setVisible(false);
      setReady(false);
    }
  });

  // Touch path: pointerdown with pointerType==="touch" shows the
  // tooltip immediately, then a document-level outside-tap dismisses.
  // Click on the wrapped element still fires — the menu (if any)
  // opens alongside the tooltip, and outside-tap then dismisses both.
  const onPointerDown = (e: PointerEvent) => {
    if (!props.mobileTap) return;
    if (e.pointerType !== "touch") return;
    cancel();
    cursorX = e.clientX;
    cursorY = e.clientY;
    reposition();
    setTouchShown(true);
    setVisible(true);
  };
  const dismissTouch = () => {
    setTouchShown(false);
    setVisible(false);
    setReady(false);
  };
  const onDocumentPointerDown = (e: PointerEvent) => {
    if (!touchShown()) return;
    const t = e.target as Node | null;
    if (tipEl?.contains(t)) return;
    if (triggerWrapEl?.contains(t)) return;
    dismissTouch();
  };
  onMount(() => {
    document.addEventListener("pointerdown", onDocumentPointerDown, true);
  });
  onCleanup(() => {
    cancel();
    document.removeEventListener("pointerdown", onDocumentPointerDown, true);
  });

  return (
    <>
      <span
        ref={(el) => (triggerWrapEl = el)}
        class={ts.triggerWrap}
        onMouseEnter={onMove}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        // mousedown collapses the tooltip immediately so the click feels
        // direct — otherwise the delayed show can pop after the user has
        // already moved on. Skipped on the touch-show path so the
        // tooltip survives the tap that opened it.
        onMouseDown={onLeave}
        onPointerDown={onPointerDown}
      >
        {props.children}
      </span>
      <Show when={visible()}>
        <Portal>
          <div
            ref={(el) => {
              tipEl = el;
              if (el) {
                // Measure on next frame so the rect is final, reposition
                // against the cursor + viewport, then reveal. Keeping the
                // node visibility:hidden until ready avoids the one-frame
                // flash where the tooltip would render past the edge.
                requestAnimationFrame(() => {
                  reposition();
                  setReady(true);
                });
              }
            }}
            class={`${ts.chartTooltip} ${ready() ? ts.tooltipVisible : ""}`}
            style={{
              left: `${pos().x}px`,
              top: `${pos().y}px`,
              visibility: ready() ? "visible" : "hidden",
            }}
          >
            <div class={ts.eventTooltipEntry}>
              <span
                class={ts.eventTooltipStripe}
                style={props.stripe ? { background: props.stripe } : undefined}
              />
              <div class={ts.eventTooltipTextBox}>
                <div class={ts.eventTooltipDesc}>{props.text}</div>
                <For each={Array.isArray(props.description) ? props.description : props.description ? [props.description] : []}>
                  {(line) => <div class={ts.eventTooltipMeta}>{line}</div>}
                </For>
                <Show when={props.properties && props.properties.length > 0}>
                  <div class={ts.eventTooltipProps}>
                    <For each={props.properties}>
                      {(p) => (
                        <>
                          <span class={ts.eventTooltipPropLabel}>{p.label}</span>
                          <span class={ts.eventTooltipPropValue}>{p.value}</span>
                        </>
                      )}
                    </For>
                  </div>
                </Show>
              </div>
            </div>
          </div>
        </Portal>
      </Show>
    </>
  );
}
